// eslint-disable react/jsx-props-no-spreading
import React, { useRef } from 'react';
import get from 'lodash/get';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Droppable, Draggable } from 'react-beautiful-dnd';
import {
  showErrorToast,
  showSuccessToast,
} from 'shared-components/BaseToast/Toast';
import { uploadAndConvertToPng } from 'services/api/admin-images';
import styles from './GalleryImages.module.css';

const MAX_IMAGE_FOR_GALLERY = 10;

const DeleteButton = ({ onClick }) => (
  <button
    type="button"
    className={classNames(styles.deleteButton, 'naked-button')}
    onClick={onClick}
  >
    <i className="fal fa-times" />
  </button>
);

const UploadButton = ({ onClick, text, error }) => (
  <button
    type="button"
    className={classNames(styles.emptyImageHolder, {
      [styles.error]: !!error,
    })}
    onClick={onClick}
  >
    <i className="fal fa-arrow-circle-up" />
    <span>{text}</span>
  </button>
);

const GalleryImages = ({
  galleryUrls,
  handleRemove,
  validateImage,
  handleUpload,
}) => {
  const inputGalleryRef = useRef(null);

  const openFileDialog = () => {
    inputGalleryRef.current.click();
  };

  const uploadToCDN = async (formData, imageCount) => {
    try {
      const response = await uploadAndConvertToPng(formData);
      const pngs = get(response, 'data.items[0].convertedUploadedUrls', []);
      const jpegs = get(response, 'data.items[0].originalUploadedUrls', []);
      handleUpload(jpegs, pngs);
      // we only want to show success toast if all images initially uploaded are successful
      // some may have failed in previous FE validation step
      if ([...jpegs, ...pngs].length === imageCount * 2) {
        showSuccessToast(
          'Upload Successful',
          'All images have been successfully uploaded.'
        );
      }
    } catch (e) {
      const errors = get(e, 'error.errors', []);
      const pngs = get(e, 'data.items[0].convertedUploadedUrls', []);
      const jpegs = get(e, 'data.items[0].originalUploadedUrls', []);
      const partialSuccess = !![...jpegs, ...pngs].length;
      if (partialSuccess) {
        handleUpload(jpegs, pngs);
      }
      const errorTitle = partialSuccess
        ? 'Image Upload Partially Successful'
        : 'Image Upload Error';
      errors.map((error) =>
        showErrorToast(errorTitle, get(error, 'message', ''), false)
      );
    }
  };

  const handleUploadImage = async (e) => {
    const formData = new FormData();
    const images = Array.from(e.target.files);
    const maxImageCount = MAX_IMAGE_FOR_GALLERY - galleryUrls.length;
    let successCount = 0;
    if (images.length > maxImageCount) {
      return showErrorToast(
        'Image Upload Quantity Exceeded',
        `You have exceeded the maximum number of images allowed to be uploaded. Please try again with only ${maxImageCount} images.`,
        false
      );
    }
    await Promise.all(
      images.map(async (image) => {
        const error = await validateImage(image, 'gallery');
        if (error) {
          showErrorToast(
            `Image Upload Error: ${image.name}`,
            `This image could not be uploaded. ${error.subtitle}`,
            false
          );
        } else {
          successCount += 1;
          formData.append('files', image);
        }
      })
    );
    if (successCount) {
      uploadToCDN(formData, images.length);
    }
  };

  const hasGalleryImages = !!galleryUrls?.length;
  const uploadEnabled = galleryUrls?.length < MAX_IMAGE_FOR_GALLERY;

  return (
    <div className={styles.galleryContainer}>
      <label htmlFor="galleryInput" className={styles.label}>
        Gallery Images
        <input
          ref={inputGalleryRef}
          type="file"
          accept="image/jpeg, image/png, image/GIF, image/jpg"
          className={styles.galleryfileDialog}
          onChange={handleUploadImage}
          disabled={!uploadEnabled}
          name="galleryInput"
          multiple
        />
      </label>
      <Droppable
        droppableId="galleryUrls"
        isDropDisabled={!uploadEnabled}
        direction="horizontal"
      >
        {(provided) => (
          <div
            {...provided.droppableProps}
            ref={provided.innerRef}
            className={styles.galleryImgsContainer}
          >
            {hasGalleryImages ? (
              galleryUrls.map((url, index) => {
                return (
                  <Draggable key={url} draggableId={url} index={index}>
                    {(draggableProvided) => (
                      <div
                        ref={draggableProvided.innerRef}
                        {...draggableProvided.draggableProps}
                        {...draggableProvided.dragHandleProps}
                        className={styles.imageContainer}
                      >
                        <img alt="thumbnail" src={url} />
                        <DeleteButton
                          onClick={() => handleRemove(index, 'galleryUrls')}
                        />
                      </div>
                    )}
                  </Draggable>
                );
              })
            ) : (
              <UploadButton
                text="Upload Gallery Image(s)"
                onClick={openFileDialog}
              />
            )}
            {hasGalleryImages && uploadEnabled && (
              <button
                type="button"
                className={styles.gallerySmallFilePicker}
                onClick={openFileDialog}
              >
                <i className="fal fa-plus" />
              </button>
            )}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    </div>
  );
};

GalleryImages.propTypes = {
  thumbnailImage: PropTypes.string,
  galleryUrls: PropTypes.arrayOf(PropTypes.string),
  handleRemove: PropTypes.func.isRequired,
  validateImage: PropTypes.func.isRequired,
  handleUpload: PropTypes.func.isRequired,
};

GalleryImages.defaultProps = {
  thumbnailImage: '',
  galleryUrls: [],
};

export default GalleryImages;
